import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Lightbulb, Loader2, Copy, Check, ArrowRight, Newspaper, Flame, Link2, RefreshCw, CalendarDays, Search, ChevronLeft, ChevronRight, Bookmark, Target } from "lucide-react";
import { generateIdeas, getDailyIdeas, getDailyIdeasByDate, saveIdea, listSavedIdeas, CATEGORIES, TIPI } from "@/lib/api";
import { toast } from "sonner";

const FILTERS = ["Tutte", ...CATEGORIES];

const CAT_COLOR = {
  Connessione: "#6B7280",
  "Tabù di coppia": "#D92D20",
  Sistema: "#D97706",
};

export const TIPO_COLOR = {
  Attenzione: "#D92D20",
  Educazione: "#2563EB",
  Autorevolezza: "#7C3AED",
  Conversione: "#16A34A",
};

export function TipoBadge({ tipo }) {
  if (!tipo) return null;
  const c = TIPO_COLOR[tipo] || "#6B7280";
  return (
    <span
      className="text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border"
      style={{ color: c, borderColor: `${c}66` }}
    >
      {tipo}
    </span>
  );
}

// Obiettivo e metrica (KPI) per ogni tipo del funnel: aiuta a giudicare il reel sulla metrica giusta
export const TIPO_KPI = {
  Attenzione: { obiettivo: "Reach + dibattito", kpi: "Views, commenti, follower" },
  Educazione: { obiettivo: "Utilità", kpi: "Salvati" },
  Autorevolezza: { obiettivo: "Fiducia", kpi: "Visite profilo, DM, commenti di qualità" },
  Conversione: { obiettivo: "Lead", kpi: "DM / parola chiave — le views saranno basse, non giudicarlo su quelle" },
};

export function TipoKpi({ tipo, className = "" }) {
  const k = TIPO_KPI[tipo];
  if (!k) return null;
  const c = TIPO_COLOR[tipo] || "#6B7280";
  return (
    <p className={`text-[0.7rem] text-neutral-400 flex items-start gap-1.5 ${className}`} data-testid="tipo-kpi">
      <Target size={12} className="mt-0.5 shrink-0" style={{ color: c }} />
      <span><span className="text-neutral-500">Obiettivo ({tipo}):</span> {k.obiettivo} · <span className="text-neutral-500">misura:</span> {k.kpi}</span>
    </p>
  );
}

function CopyIcon({ text }) {
  const [c, setC] = useState(false);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(text || ""); setC(true); toast.success("Hook copiato"); setTimeout(() => setC(false), 1500); }}
      className="text-neutral-500 hover:text-white transition-colors"
      title="Copia hook"
    >
      {c ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
}

export function IdeaCard({ idea, i, onUseIdea, onSave, isSaved }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05 }}
      className="bg-[#121212] border border-[#262626] rounded-md p-5 flex flex-col group hover:border-[#333] transition-colors"
      data-testid={`idea-card-${i}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="text-[0.6rem] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm"
            style={{ background: `${CAT_COLOR[idea.categoria] || "#6B7280"}22`, color: CAT_COLOR[idea.categoria] || "#9CA3AF" }}
          >
            {idea.categoria}
          </span>
          <TipoBadge tipo={idea.tipo} />
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <CopyIcon text={idea.hook_fissato} />
          {onSave && (
            <button
              data-testid={`save-idea-${i}`}
              onClick={() => onSave(idea)}
              className={`transition-colors ${isSaved ? "text-[#D92D20]" : "text-neutral-500 hover:text-white"}`}
              title={isSaved ? "Già salvata" : "Salva idea"}
            >
              <Bookmark size={15} fill={isSaved ? "#D92D20" : "none"} />
            </button>
          )}
        </div>
      </div>

      <p className="font-display font-extrabold text-lg leading-tight text-white mb-3">
        {idea.hook_fissato}
      </p>

      <p className="text-sm text-neutral-300 mb-2">{idea.angolo}</p>

      {idea.protagonista && (
        <p className="text-xs text-neutral-500 mb-1">
          <span className="text-neutral-600">Protagonista:</span> {idea.protagonista}
        </p>
      )}
      {idea.perche_funziona && (
        <p className="text-xs text-neutral-500 italic mb-3">{idea.perche_funziona}</p>
      )}
      {idea.spunto && (
        <p className="text-[0.7rem] text-neutral-400 bg-[#1A1A1A] border border-[#262626] rounded-sm px-2.5 py-1.5 mb-4 flex items-start gap-1.5">
          <Newspaper size={12} className="text-[#D97706] mt-0.5 shrink-0" />
          <span><span className="text-neutral-500">Spunto:</span> {idea.spunto}</span>
        </p>
      )}

      <TipoKpi tipo={idea.tipo} className="mb-3" />

      <button
        data-testid={`use-idea-${i}`}
        onClick={() => onUseIdea(idea)}
        className="mt-auto self-start flex items-center gap-1.5 text-sm font-semibold text-[#D92D20] hover:gap-2.5 transition-all"
      >
        Scrivi lo script <ArrowRight size={15} />
      </button>
    </motion.div>
  );
}

function formatDate(dstr) {
  try {
    return new Date(dstr + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return dstr;
  }
}

function shiftDate(dstr, days) {
  const d = new Date(dstr + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function Ideas({ onUseIdea, onSavedChange }) {
  const [daily, setDaily] = useState([]);
  const [dailyDate, setDailyDate] = useState("");
  const [todayStr, setTodayStr] = useState("");
  const [fonti, setFonti] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savedHooks, setSavedHooks] = useState(new Set());

  // Manual search
  const [showSearch, setShowSearch] = useState(false);
  const [categoria, setCategoria] = useState("Tutte");
  const [tipoFiltro, setTipoFiltro] = useState("Tutti");
  const [focus, setFocus] = useState("");
  const [url, setUrl] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchIdee, setSearchIdee] = useState([]);

  const isToday = !todayStr || dailyDate === todayStr;

  const loadSaved = useCallback(async () => {
    try {
      const saved = await listSavedIdeas();
      setSavedHooks(new Set(saved.map((s) => s.hook_fissato)));
    } catch { /* ignore */ }
  }, []);

  const loadDaily = useCallback(async (refresh) => {
    if (refresh) setRefreshing(true); else setDailyLoading(true);
    try {
      const data = await getDailyIdeas(refresh);
      setDaily(data.idee || []);
      setDailyDate(data.date || "");
      setTodayStr(data.date || "");
      setFonti(data.fonti || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento delle idee");
    } finally {
      setDailyLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadByDate = useCallback(async (date) => {
    setDailyLoading(true);
    try {
      const data = await getDailyIdeasByDate(date);
      setDaily(data.idee || []);
      setDailyDate(data.date || date);
      setFonti(data.fonti || []);
    } catch (e) {
      toast.error("Errore nel caricamento");
    } finally {
      setDailyLoading(false);
    }
  }, []);

  useEffect(() => { loadDaily(false); loadSaved(); }, [loadDaily, loadSaved]);

  const goPrev = () => loadByDate(shiftDate(dailyDate, -1));
  const goNext = () => { const n = shiftDate(dailyDate, 1); if (n <= todayStr) loadByDate(n); };
  const goToday = () => loadByDate(todayStr);

  const onSave = async (idea) => {
    try {
      await saveIdea(idea);
      setSavedHooks((prev) => new Set(prev).add(idea.hook_fissato));
      toast.success("Idea salvata");
      onSavedChange?.();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const onSearch = async () => {
    setSearchLoading(true);
    try {
      const data = await generateIdeas({ categoria, tipo: tipoFiltro, focus, url, quantita: 6 });
      setSearchIdee(data.idee || []);
      if (data.nota) toast.message(data.nota);
      if (!data.idee?.length) toast.error("Nessuna idea generata, riprova");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nella generazione");
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <div>
      {/* ---- IDEE DEL GIORNO ---- */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Flame size={22} className="text-[#D92D20]" />
            <h2 className="font-display font-extrabold text-3xl tracking-tight">
              {isToday ? "Le idee di oggi" : "Idee del giorno"}
            </h2>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button data-testid="day-prev" onClick={goPrev} className="text-neutral-400 hover:text-white border border-[#333] hover:border-[#555] rounded-sm p-1 transition-colors" title="Giorno precedente">
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm text-neutral-300 capitalize flex items-center gap-1.5 min-w-[180px]">
              <CalendarDays size={14} className="text-neutral-600" />
              {dailyDate ? formatDate(dailyDate) : "—"}
            </span>
            <button
              data-testid="day-next"
              onClick={goNext}
              disabled={dailyDate >= todayStr}
              className="text-neutral-400 hover:text-white border border-[#333] hover:border-[#555] rounded-sm p-1 transition-colors disabled:opacity-30 disabled:hover:border-[#333]"
              title="Giorno successivo"
            >
              <ChevronRight size={16} />
            </button>
            {!isToday && (
              <button data-testid="day-today" onClick={goToday} className="text-xs font-semibold text-[#D92D20] hover:text-white transition-colors ml-1">
                Torna a oggi
              </button>
            )}
          </div>
          {fonti.length > 0 && (
            <p className="text-xs text-neutral-600 mt-2 flex items-center gap-1.5">
              <Newspaper size={12} className="text-[#D97706]" />
              Ispirate alle news: <span className="text-neutral-500">{fonti.join(" · ")}</span>
            </p>
          )}
        </div>
        {isToday && (
          <button
            data-testid="refresh-daily-btn"
            onClick={() => loadDaily(true)}
            disabled={refreshing || dailyLoading}
            className="self-start sm:self-auto border border-[#333] hover:border-[#D92D20] text-neutral-300 hover:text-white font-semibold py-2.5 px-5 rounded-sm flex items-center gap-2 transition-colors text-sm disabled:opacity-60"
          >
            {refreshing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            Proponimene altre
          </button>
        )}
      </div>

      {dailyLoading ? (
        <div className="border border-[#262626] rounded-md min-h-[260px] flex flex-col items-center justify-center">
          <Loader2 className="animate-spin text-[#D92D20] mb-4" size={30} />
          <p className="text-neutral-400">Sto preparando le idee scomode...</p>
        </div>
      ) : daily.length === 0 ? (
        <div className="border border-dashed border-[#262626] rounded-md min-h-[200px] flex flex-col items-center justify-center text-center p-8">
          <CalendarDays size={34} className="text-neutral-700 mb-3" />
          <p className="text-neutral-500">Nessuna idea archiviata per questo giorno.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="daily-ideas-list">
          {daily.map((idea, i) => (
            <IdeaCard key={`${dailyDate}-${i}`} idea={idea} i={i} onUseIdea={onUseIdea} onSave={onSave} isSaved={savedHooks.has(idea.hook_fissato)} />
          ))}
        </div>
      )}

      {/* ---- CERCA IDEE SU UN TEMA (opzionale) ---- */}
      <div className="mt-10 border-t border-[#262626] pt-8">
        <button
          data-testid="toggle-search"
          onClick={() => setShowSearch((s) => !s)}
          className="flex items-center gap-2 text-neutral-300 hover:text-white transition-colors font-semibold"
        >
          <Search size={18} />
          Cerca idee su un tema specifico
          <span className="text-xs text-neutral-600 font-normal">(o da un link/news)</span>
        </button>

        {showSearch && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="overflow-hidden">
            <div className="bg-[#121212] border border-[#262626] rounded-md p-6 mt-5">
              <label className="label-caps block mb-2">Categoria</label>
              <div className="flex flex-wrap gap-2 mb-5">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    data-testid={`idea-cat-${f}`}
                    onClick={() => setCategoria(f)}
                    className={`text-sm font-semibold py-2 px-4 rounded-sm border transition-colors ${
                      categoria === f
                        ? "bg-[#D92D20] border-[#D92D20] text-white"
                        : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>

              <label className="label-caps block mb-2">Tipo di contenuto</label>
              <div className="flex flex-wrap gap-2 mb-5">
                {["Tutti", ...TIPI].map((t) => (
                  <button
                    key={t}
                    data-testid={`idea-tipo-${t}`}
                    onClick={() => setTipoFiltro(t)}
                    className={`text-sm font-semibold py-2 px-4 rounded-sm border transition-colors ${
                      tipoFiltro === t
                        ? "bg-white border-white text-black"
                        : "bg-transparent border-[#333] text-neutral-400 hover:border-neutral-500"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <label className="label-caps mb-2 flex items-center gap-2">
                <Link2 size={13} /> Link discussione / news (opzionale)
              </label>
              <input
                data-testid="idea-url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Es. un articolo o un post; se il sito blocca la lettura, incolla il testo qui sotto"
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-4"
              />

              <label className="label-caps mb-2 flex items-center gap-2">
                <Newspaper size={13} /> Focus / testo (opzionale)
              </label>
              <textarea
                data-testid="idea-focus"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                rows={2}
                placeholder="Es. tradimenti, carico mentale della donna, mutuo, figli e stanchezza..."
                className="w-full bg-[#0A0A0A] border border-[#262626] rounded-sm p-3 text-sm text-neutral-100 focus:outline-none focus:border-[#D92D20] mb-5 resize-none"
              />

              <button
                data-testid="idea-generate-btn"
                onClick={onSearch}
                disabled={searchLoading}
                className="bg-[#D92D20] hover:bg-[#B42318] disabled:opacity-60 text-white font-bold py-3 px-8 rounded-sm flex items-center gap-2 transition-colors uppercase tracking-wider text-sm"
              >
                {searchLoading ? <Loader2 className="animate-spin" size={18} /> : <Lightbulb size={18} />}
                {searchLoading ? "Cerco idee..." : "Genera idee"}
              </button>
            </div>

            {searchIdee.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5" data-testid="ideas-list">
                {searchIdee.map((idea, i) => (
                  <IdeaCard key={`s-${i}`} idea={idea} i={i} onUseIdea={onUseIdea} onSave={onSave} isSaved={savedHooks.has(idea.hook_fissato)} />
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
